import {
  Box,
  Card,
  Flex,
  Link,
  Typography as ReeferTypography,
  StarFilledIcon,
  useDesktopMediaQuery,
  useTabletMediaQuery,
} from '@iheartjane/reefer';
import type { ColorKey } from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import { clsx } from 'clsx';
import type { ComponentType } from 'react';
import { useState } from 'react';

import type { CustomImageAdViewModel } from '../customImageAd.types';

import styles from '../customImageAd.module.css';

interface AdProductCardProps {
  isKiosk?: boolean;
  product: CustomImageAdViewModel;
  typography?: ComponentType;
}

const LINEAGE_COLORS: Record<string, ColorKey> = {
  indica: 'grape',
  sativa: 'emerald',
};

export const AdProductCard = ({
  isKiosk,
  product,
  typography,
}: AdProductCardProps) => {
  const isTablet = useTabletMediaQuery({});
  const isDesktop = useDesktopMediaQuery({});
  const [imageLoaded, setImageLoaded] = useState(false);
  const Typography = typography || ReeferTypography;

  return (
    <Link
      className={clsx(styles['fullSize'], styles['imageWithLoading'], {
        [styles['imageLoaded']]: imageLoaded,
        [styles['imageNotLoaded']]: !imageLoaded,
      })}
      to={product.pdpUrl}
      variant="minimal"
      onClick={(e) => {
        product.clickHandler();
        e.stopPropagation();
      }}
      style={{ height: '100%', pointerEvents: 'auto' }}
    >
      <Card
        className={clsx(styles['cardAspectRatio'])}
        height="100%"
        flat
        style={{
          background: 'white',
          borderRadius: '24px',
          cursor: 'pointer',
        }}
      >
        <Card.Image
          alt={product.name}
          src={product.image_urls[0]}
          responsive
          onLoad={() => setImageLoaded(true)}
        />

        <Card.Content height="100%">
          <Flex justifyContent="space-between" width="100%">
            <Typography
              variant={isKiosk ? 'small-body-semibold' : 'body-semibold'}
              color={
                product.category
                  ? LINEAGE_COLORS[product.category.toLowerCase()] ||
                    'grays-black'
                  : 'grays-black'
              }
              maxLines={1}
            >
              {product.category ? sentenceCase(product.category) : ''}
            </Typography>

            {isDesktop &&
              product.aggregate_rating > 0 &&
              product.review_count > 5 && (
                <Flex flexDirection="row" alignItems="center" ml="auto">
                  <StarFilledIcon size="sm" />
                  <Typography
                    variant={isKiosk ? 'small-body-regular' : 'body-regular'}
                    color="grays-dark"
                    ml={4}
                    maxLines={1}
                  >
                    {product.aggregate_rating} ({product.review_count})
                  </Typography>
                </Flex>
              )}
          </Flex>

          {isTablet && (
            <Typography
              variant={isKiosk ? 'small-body-semibold' : 'body-semibold'}
              maxLines={isDesktop ? 2 : 1}
            >
              {product.name}
            </Typography>
          )}
        </Card.Content>
      </Card>
    </Link>
  );
};

export const PlaceHolderAdProductCard = () => {
  return (
    <Flex
      className={styles['cardAspectRatio']}
      flexDirection="column"
      height="100%"
      overflow="hidden"
      borderRadius="lg"
      data-testid="placeholder-card"
    >
      <Box height="60%" background="grays-light" />
      <Flex
        flexDirection="column"
        height="40%"
        p={16}
        gap={8}
        background="grays-white"
      >
        {['33%', '93%', '43%'].map((width) => (
          <Box
            key={width}
            width={width}
            height={22}
            borderRadius="lg"
            background="grays-light"
          />
        ))}
      </Flex>
    </Flex>
  );
};
